"use client";

import { useEffect, useRef, type CSSProperties } from "react";
import gsap from "gsap";
import { afterSplash } from "@/lib/splash";
import { cn } from "@/lib/utils";

/**
 * An isometric staircase of phrases. Each phrase sits on its own step and stays
 * there; when the stack comes into view the phrases drop into their steps one
 * after another, and they lift back out when it leaves, so returning to the
 * section replays the fall. Nothing loops, so whichever phrase the caller marks
 * `emphasis` is on screen the whole time.
 *
 * The visual stack is `aria-hidden` and `srText` carries the real sentence,
 * because glyphs sheared 30 degrees read fine to eyes and terribly to a screen
 * reader's virtual cursor.
 *
 * Sizes come in pairs: the plain prop applies from the md breakpoint up, the
 * `Md`-suffixed one below it. They are passed down as custom properties rather
 * than branched in JS, so resizing the window cannot leave the type at the
 * wrong size.
 */

export type LayeredTextLine = {
  text: string;
  /** Full-ink phrases. Once any phrase is emphasised, the rest sit back at low opacity. */
  emphasis?: boolean;
};

export interface LayeredTextProps {
  lines?: LayeredTextLine[];
  /** The sentence assistive tech should read. Without it the stack is silent. */
  srText?: string;
  /** Gate for the animation. Off renders the phrases at rest in their steps. */
  active?: boolean;
  /** Seconds between entering the viewport and the first phrase dropping. */
  startDelay?: number;
  /** Type size at md and above. */
  fontSize?: string;
  /** Type size below md. */
  fontSizeMd?: string;
  /** Step height at md and above. Doubles as the distance a phrase falls. */
  lineHeight?: number;
  /** Step height below md. */
  lineHeightMd?: number;
  /** Horizontal step between rows, which is what makes the stack lean. */
  stepX?: number;
  stepXMd?: number;
  /** Flip the lean, so the staircase recedes to the left instead of the right. */
  mirror?: boolean;
  className?: string;
}

export function LayeredText({
  lines = [
    { text: "INFINITE" },
    { text: "PROGRESS" },
    { text: "INNOVATION" },
    { text: "FUTURE" },
    { text: "DREAMS" },
    { text: "ACHIEVEMENT" },
  ],
  srText,
  active = true,
  startDelay = 0,
  fontSize = "72px",
  fontSizeMd = "36px",
  lineHeight = 60,
  lineHeightMd = 35,
  stepX = 35,
  stepXMd = 20,
  mirror = false,
  className,
}: LayeredTextProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  // Estimated from the longest phrase, since measuring would mean a layout pass on
  // every resize for a value that only needs to be roughly right.
  const widest = lines.reduce((n, line) => Math.max(n, line.text.length), 0);
  const overhang = widest * parseFloat(fontSize) * 0.48;
  const overhangMd = widest * parseFloat(fontSizeMd) * 0.48;

  const hasEmphasis = lines.some((line) => line.emphasis);

  // The stack pivots on its emphasised phrases rather than on its midpoint, so
  // the line that matters stays centred however many phrases are added around
  // it. Without this the caller would have to keep the list balanced by hand.
  const emphasisIndices = lines.reduce<number[]>((acc, line, i) => {
    if (line.emphasis) acc.push(i);
    return acc;
  }, []);
  const midpoint = (lines.length - 1) / 2;
  const pivot = emphasisIndices.length
    ? emphasisIndices.reduce((sum, i) => sum + i, 0) / emphasisIndices.length
    : midpoint;
  // Rows above the pivot outnumber those below (or the reverse), so the stack is
  // pushed back by the difference to put the pivot on the centre line.
  const shift = midpoint - pivot;

  useEffect(() => {
    const container = containerRef.current;
    if (!container || !active) return;

    const ctx = gsap.context(() => {
      const rows = gsap.utils.toArray<HTMLElement>("[data-row]");
      const mm = gsap.matchMedia();

      /**
       * The fall distance is the step height, because one step up is exactly out of
       * the clipped window. Deriving it means a caller can change the size without
       * the fall landing half way.
       *
       * Under reduced motion nothing is built at all, so the phrases simply rest in
       * their steps.
       */
      mm.add(
        {
          isDesktop: "(min-width: 768px)",
          isReduced: "(prefers-reduced-motion: reduce)",
        },
        (self) => {
          const { isDesktop, isReduced } = self.conditions as Record<string, boolean>;
          if (isReduced) return;

          // The delay lives in the position parameter rather than on the timeline,
          // so reverse() walks back through it without any surprises.
          const tl = gsap.timeline({ paused: true }).from(
            rows,
            {
              y: -(isDesktop ? lineHeight : lineHeightMd),
              duration: 0.7,
              ease: "power3.out",
              stagger: 0.09,
            },
            startDelay,
          );

          // The first fall waits for the first-load splash to lift, or it plays out unseen.
          let waiting = () => {};
          const io = new IntersectionObserver(
            ([entry]) => {
              waiting();
              if (entry.isIntersecting) waiting = afterSplash(() => tl.play());
              else tl.reverse();
            },
            { threshold: 0.3 },
          );
          io.observe(container);

          return () => {
            waiting();
            io.disconnect();
          };
        },
      );
    }, container);

    return () => ctx.revert();
  }, [lines, lineHeight, lineHeightMd, active, startDelay]);

  return (
    <div
      ref={containerRef}
      style={
        {
          "--lt-fs": fontSize,
          "--lt-fs-md": fontSizeMd,
          "--lt-lh": `${lineHeight}px`,
          "--lt-lh-md": `${lineHeightMd}px`,
          // The line box matches the step height. Anything tighter and the
          // overflow-hidden that clips the falling phrase starts biting into the
          // glyphs at rest.
          "--lt-lead": `${lineHeight}px`,
          "--lt-lead-md": `${lineHeightMd}px`,
          "--lt-overhang": `${Math.round(overhang * 0.58)}px`,
          "--lt-overhang-md": `${Math.round(overhangMd * 0.58)}px`,
          "--lt-shift": `${Math.round(shift * lineHeight)}px`,
          "--lt-shift-md": `${Math.round(shift * lineHeightMd)}px`,
        } as CSSProperties
      }
      className={cn(
        "select-none font-black uppercase tracking-[-2px] antialiased",
        "font-[family-name:var(--font-roboto)]",
        "[--stroke:1.5px] [-webkit-text-stroke:var(--stroke)] [paint-order:stroke_fill]",
        "text-[length:var(--lt-fs-md)] md:text-[length:var(--lt-fs)]",
        className,
      )}
    >
      {srText && <span className="sr-only">{srText}</span>}
      {/* The skew tilts each row by 30 degrees, so a row sprawls about width*tan(30deg)
          past its own box: down on one side, up on the other. Nothing around it would
          know to move, so the overhang is reserved here as padding. It is symmetric so
          that centring the box also centres what you see. */}
      <ul
        aria-hidden
        className={cn(
          "m-0 flex list-none flex-col items-center px-0",
          "py-[var(--lt-overhang-md)] md:py-[var(--lt-overhang)]",
          "translate-y-[var(--lt-shift-md)] md:translate-y-[var(--lt-shift)]",
        )}
      >
        {lines.map((line, index) => {
          const centre = pivot;
          // Mirroring negates both skew angles and the horizontal step together;
          // flipping only one of them would shear the staircase instead of turning it.
          const dir = mirror ? -1 : 1;
          // Skew alternates so consecutive steps read as the two faces of an isometric
          // block; scaleY compensates for the height the skew adds or removes.
          const skew =
            index % 2 === 0
              ? `skew(${60 * dir}deg, ${-30 * dir}deg) scaleY(0.66667)`
              : `skew(0deg, ${-30 * dir}deg) scaleY(1.33333)`;
          return (
            <li
              key={`${line.text}-${index}`}
              style={
                {
                  "--lt-tf": `translateX(${(index - centre) * stepX * dir}px) ${skew}`,
                  "--lt-tf-md": `translateX(${(index - centre) * stepXMd * dir}px) ${skew}`,
                } as CSSProperties
              }
              className={cn(
                "relative overflow-hidden",
                "h-[var(--lt-lh-md)] md:h-[var(--lt-lh)]",
                "[transform:var(--lt-tf-md)] md:[transform:var(--lt-tf)]",
              )}
            >
              <p
                data-row
                className={cn(
                  "m-0 whitespace-nowrap px-[15px] align-top",
                  "h-[var(--lt-lh-md)] md:h-[var(--lt-lh)]",
                  "leading-[var(--lt-lead-md)] md:leading-[var(--lt-lead)]",
                  hasEmphasis && !line.emphasis && "opacity-25",
                )}
              >
                {line.text}
              </p>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
